import { AlertTriangle, RefreshCw } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SchemaMismatchErrorProps {
  errorMessage?: string;
}

export default function SchemaMismatchError({
  errorMessage,
}: SchemaMismatchErrorProps) {
  return (
    <div className="flex-1 flex flex-col min-h-0 bg-background">
      <ScrollArea className="flex flex-col flex-1">
        <div className="max-w-md mx-auto px-6 py-8 space-y-6">
          {/* Error Icon */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-destructive/10 rounded-2xl">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-foreground">
                Anwendung veraltet
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Die Anwendung muss aktualisiert werden
              </p>
            </div>
          </div>

          {/* Error Details */}
          {errorMessage && (
            <div className="bg-muted/50 border rounded-lg p-4 space-y-2">
              <h3 className="text-xs font-medium text-foreground">
                Technische Details
              </h3>
              <p className="text-xs text-muted-foreground font-mono break-all">
                {errorMessage}
              </p>
            </div>
          )}

          {/* Explanation */}
          <div className="bg-card border rounded-lg shadow-sm p-5 space-y-4">
            <h2 className="text-sm font-medium text-foreground">
              Was ist passiert?
            </h2>
            <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
              <p>
                Die Anwendung wurde zwischenzeitlich aktualisiert. Ihr Browser
                verwendet noch eine ältere Version.
              </p>
              <p>
                Um die Anwendung weiterhin nutzen zu können, muss Ihr Browser
                die neueste Version laden.
              </p>
            </div>
          </div>

          {/* Instructions for Outlook on Windows */}
          <div className="bg-card border rounded-lg shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-medium text-foreground">
                So beheben Sie das Problem
              </h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium text-xs">
                  1
                </div>
                <p className="text-sm text-foreground leading-relaxed pt-0.5">
                  Schließen Sie das lytra Add-in
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium text-xs">
                  2
                </div>
                <div className="space-y-2 pt-0.5">
                  <p className="text-sm text-foreground leading-relaxed">
                    Löschen Sie den Internet Explorer Cache
                  </p>
                  <div className="pl-4 space-y-1 text-xs text-muted-foreground">
                    <p>• Öffnen Sie die Windows-Systemsteuerung</p>
                    <p>
                      • Gehen Sie zu{" "}
                      <span className="font-medium text-foreground">
                        Internetoptionen
                      </span>
                    </p>
                    <p>
                      • Klicken Sie im Tab{" "}
                      <span className="font-medium text-foreground">
                        Allgemein
                      </span>{" "}
                      auf{" "}
                      <span className="font-medium text-foreground">
                        Löschen...
                      </span>
                    </p>
                    <p>
                      • Wählen Sie{" "}
                      <span className="font-medium text-foreground">
                        Temporäre Internetdateien
                      </span>
                    </p>
                    <p>
                      • Klicken Sie auf{" "}
                      <span className="font-medium text-foreground">
                        Löschen
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium text-xs">
                  3
                </div>
                <p className="text-sm text-foreground leading-relaxed pt-0.5">
                  Starten Sie Outlook neu
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium text-xs">
                  4
                </div>
                <p className="text-sm text-foreground leading-relaxed pt-0.5">
                  Öffnen Sie das lytra Add-in erneut
                </p>
              </div>
            </div>
          </div>

          {/* Alternative for Web Browsers */}
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
            <h3 className="text-xs font-medium text-blue-900 dark:text-blue-100">
              Alternative für Webbrowser
            </h3>
            <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
              Falls Sie einen Webbrowser (Chrome, Firefox, Edge) verwenden,
              können Sie auch einfach{" "}
              <span className="font-medium">Strg+Shift+R</span> (Windows) oder{" "}
              <span className="font-medium">Cmd+Shift+R</span> (Mac) drücken,
              um die Seite mit leerem Cache neu zu laden.
            </p>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
